import { useRef, useState } from 'react';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';

/**
 * The feedback widget: a bubble in the corner that opens a small form.
 *
 * The bubble and the dialog are the shell's, not a page's — the EJS layout draws them for every signed-in page,
 * which is the point of a feedback control: the page you were on when something went wrong is the context that
 * makes the report useful. The attachment is carried as a DATA URL (the client downscales it first), so both front
 * ends submit exactly the same payload to the same service.
 */
/**
 * The launcher's three styles are a site setting the admin page writes (`bubble`, `tab`, `none`). The EJS layout
 * decides at render time which element to draw and keeps `body.fb-bubble-on` in step; the SPA draws the same three
 * and listens for the setting changing under it, so the admin page's buttons take effect on the spot rather than on
 * the next page load.
 */
export const FEEDBACK_WIDGET_EVENT = 'pf:feedback-widget';

export type FeedbackVariant = 'bubble' | 'tab' | 'none';

export function FeedbackWidget({ path, variant = 'bubble' }: { path: string; variant?: FeedbackVariant }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'bug' | 'feature'>('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setTitle('');
    setBody('');
    setImage(null);
    setSize(null);
    setError(null);
    setType('bug');
    if (fileRef.current) fileRef.current.value = '';
  };

  // A file becomes a data URL through a canvas, downscaled to 1600px on its long edge: a phone screenshot is
  // several MB of pixels that a 4 MB request would refuse.
  const pickImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setImage(String(reader.result));
          setSize({ w: img.width, h: img.height });
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        setImage(canvas.toDataURL('image/png'));
        setSize({ w, h });
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    const res = await fetch('/api/v1/feedback', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        type,
        title,
        body,
        page_context: path,
        image: image || undefined,
        image_width: size?.w,
        image_height: size?.h,
      }),
    });
    const json = await res.json().catch(() => null);
    setBusy(false);
    if (!res.ok || !json?.ok) {
      setError((json && json.error) || `HTTP ${res.status}`);
      return;
    }
    reset();
    setOpen(false);
  };

  if (variant === 'none') return null;

  return (
    <>
      {variant === 'tab' ? (
        <button type="button" id="fb-edge-tab" className="fb-launcher" title="Send feedback" onClick={() => setOpen(true)}>
          {'SEND\u00A0FEEDBACK'}
        </button>
      ) : (
      <button
        type="button"
        id="fb-bubble"
        className="fb-launcher"
        title="Send feedback"
        aria-label="Send feedback"
        onClick={() => setOpen(true)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.4 9.4 0 0 1-3.8-.8L3 21l1.9-4.9a8.4 8.4 0 0 1-.9-3.8 8.4 8.4 0 0 1 8.4-9 8.4 8.4 0 0 1 8.6 8.2z" />
        </svg>
      </button>
      )}

      <Modal
        id="feedback-modal"
        open={open}
        onClose={() => setOpen(false)}
        title="Send feedback"
        subtitle="A bug, or something that would make the app better."
        size="sm"
      >
        {error ? (
          <div className="error" id="feedback-error" style={{ marginBottom: 10 }}>
            {error}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, margin: '0 0 10px' }}>
          {(['bug', 'feature'] as const).map((t) => (
            <Button
              key={t}
              id={`feedback-type-${t}`}
              variant={type === t ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setType(t)}
            >
              {t === 'bug' ? 'Bug' : 'Feature request'}
            </Button>
          ))}
        </div>
        <Field label="Title" htmlFor="feedback-title">
          <input id="feedback-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="What happened?" htmlFor="feedback-body">
          <textarea id="feedback-body" rows={4} value={body} maxLength={5000} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <Field label="Screenshot (optional)" htmlFor="feedback-image">
          <input
            id="feedback-image"
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) pickImage(f);
            }}
          />
        </Field>
        <div className="modal-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button
            id="feedback-modal-cancel"
            variant="secondary"
            onClick={() => {
              reset();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button disabled={busy || !title.trim() || !body.trim()} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </Modal>
    </>
  );
}
